/* eslint-disable react/no-array-index-key */
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Link from './Link';

type PaginationProps = {
  currentPage: number,
  totalPages: number,
  getPageUrl: (page: number) => string,
  previousText: string,
  nextText: string,
}

const ArrowLeft = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L8.41421 9H14C14.5523 9 15 9.44772 15 10C15 10.5523 14.5523 11 14 11H8.41421L10.7071 13.2929C11.0976 13.6834 11.0976 14.3166 10.7071 14.7071C10.3166 15.0976 9.68342 15.0976 9.29289 14.7071L5.29289 10.7071C4.90237 10.3166 4.90237 9.68342 5.29289 9.29289L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z" />
  </svg>
);

const ArrowRight = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L14.7071 9.29289C15.0976 9.68342 15.0976 10.3166 14.7071 10.7071L10.7071 14.7071C10.3166 15.0976 9.68342 15.0976 9.29289 14.7071C8.90237 14.3166 8.90237 13.6834 9.29289 13.2929L11.5858 11H6C5.44772 11 5 10.5523 5 10C5 9.44772 5.44772 9 6 9H11.5858L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289Z" />
  </svg>
);

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 16px;
  overflow: hidden;
  background: #FFFFFF;
  box-shadow: ${(props) => props.theme.shadows.hard};
  border: 1px solid var(--color-charcoal-v10);
  padding: 16px;
  box-sizing: border-box;
  overflow-x: auto;
  * {
    transition-duration: 150ms;
    transition-property: background-color, color, border-color;
  }

  @media (max-width: 400px) {
    padding: 16px 8px;
  }
`;

const PaginationNumber = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--color-charcoal-v10);
  border-radius: 4px;
  height: 32px;
  width: 32px;
  margin-right: 4px;
  cursor: pointer;

  &, &:hover {
    color: var(--color-charcoal-v100);
  }

  &:hover {
    background-color: var(--color-charcoal-v10);
    border-color: var(--color-charcoal-v20);
  }

  &:active {
    background-color: var(--color-charcoal-v20);
    border-color: var(--color-charcoal-v30);
    transition: none;
  }

  &.current {
    background-color: var(--color-bluescape-dark);
    color: white;
    pointer-events: none;
    border-color: var(--color-bluescape-dark);
  }
`;

const PaginationNumberContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`;

const PaginationLink = styled.a<{disabled: boolean}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: var(--color-charcoal-v100);
  margin-right: 4px;
  cursor: pointer;

  &, &:hover, &:active {
    color: inherit;
  }

  pointer-events: ${(props) => (props.disabled ? 'none' : 'inherit')};
`;

const PaginationLinkWrapper = styled.div<{disabled: boolean}>`
  pointer-events: ${(props) => (props.disabled ? 'none' : 'unset')};
  color: ${(props) => (props.disabled ? 'var(--color-charcoal-v20)' : 'var(--color-charcoal-v100)')};

  &:hover {
    color: var(--color-bluescape-v100);
  }

  &:active {
    color: var(--color-bluescape-v80);
  }
`;

const PaginationOverflowDotContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 11px;
  padding-left: calc(11px - 4px);
  color: var(--color-charcoal-v100);

  @media (max-width: 400px) {
    > :first-of-type {
      display: none;
    }
  }

  @media (max-width: 360px) {
    > :not(:last-of-type) {
      display: none;
    }
  }
`;

const PaginationOverflowDot = styled.div`
  display: inline-block;
  height: 4px;
  width: 4px;
  background-color: var(--color-charcoal-v100);
  border-radius: 50%;
  &:not(:last-of-type) {
    margin-right: 4px;
  }
`;

const PaginationOverflowDots = () => (
  <PaginationOverflowDotContainer>
    <PaginationOverflowDot />
    <PaginationOverflowDot />
    <PaginationOverflowDot />
  </PaginationOverflowDotContainer>
);

const NavigationParagraph = styled.span`
  @media (max-width: ${(props) => props.theme.breakpoints.medium}) {
    display: none;
  }
`;

const Pagination = ({
  currentPage, totalPages, getPageUrl, previousText, nextText,
}: PaginationProps) => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const lastPage = Math.ceil(totalPages);
  const paginationNumbers: (number | 'overflow')[] = [];
  const pages: number[] = [];
  for (let i = 1; i <= lastPage; i += 1) {
    pages.push(i);
  }

  const maxItems = isMobile ? 7 : 9;
  const overflowCount = isMobile ? 3 : 4;
  const maxDistance = isMobile ? 1 : 2;
  const overflowShowOffset = isMobile ? 4 : 5;
  const fakeCurrentPage = Math.min(Math.max(currentPage, 3), lastPage - 2);

  if (totalPages <= maxItems) {
    pages.forEach((page) => paginationNumbers.push(page));
  } else {
    paginationNumbers.push(1);
    if (fakeCurrentPage > overflowCount) {
      if (currentPage === overflowShowOffset) {
        paginationNumbers.push(2);
      } else {
        paginationNumbers.push('overflow');
      }
    }
    pages.forEach((page) => {
      const distance = Math.abs(fakeCurrentPage - page);
      if (page === 1 || page === lastPage) {
        return;
      }

      if (distance <= maxDistance) {
        paginationNumbers.push(page);
      }
    });

    if (fakeCurrentPage <= lastPage - overflowCount) {
      if (currentPage === lastPage - overflowShowOffset + 1) {
        paginationNumbers.push(lastPage - 1);
      } else {
        paginationNumbers.push('overflow');
      }
    }
    paginationNumbers.push(lastPage);
  }

  if (paginationNumbers.length < 2) {
    return null;
  }

  return (
    <PaginationContainer>
      <PaginationLinkWrapper disabled={currentPage < 2}>
        <Link
          as={PaginationLink}
          disabled={currentPage < 2}
          href={getPageUrl(currentPage - 1 < 1 ? 1 : currentPage - 1)}
        >
          <ArrowLeft />
          <NavigationParagraph>
            {previousText}
          </NavigationParagraph>
        </Link>
      </PaginationLinkWrapper>
      <PaginationNumberContainer>
        {paginationNumbers.map((page, i) => {
          if (page === 'overflow') {
            return <PaginationOverflowDots key={i} />;
          }
          return (
            <Link
              as={PaginationNumber}
              key={i}
              className={classNames({ current: page === currentPage })}
              href={getPageUrl(page)}
            >
              {page}
            </Link>
          );
        })}
      </PaginationNumberContainer>
      <PaginationLinkWrapper disabled={currentPage >= lastPage}>
        <Link
          as={PaginationLink}
          disabled={currentPage >= lastPage}
          href={getPageUrl(currentPage + 1 > lastPage ? lastPage : currentPage + 1)}
        >
          <NavigationParagraph>
            {nextText}
          </NavigationParagraph>
          <ArrowRight />
        </Link>
      </PaginationLinkWrapper>
    </PaginationContainer>
  );
};

export default Pagination;
